/**
 * Tone driver — speelt bel- en ringbacktonen op precies ÉÉN tab, afgeleid van
 * de gespiegelde call-state (phone-state) in plaats van imperatief vanuit de
 * owner-tab.
 *
 * Historie: de tonen leefden eerst alleen op de owner-tab (Web Locks-leider).
 * Is dat een achtergrondtab waarin nooit geklikt is, dan houdt Chrome's
 * autoplay-policy de AudioContext op 'suspended' en rinkelt er niets. Daarna
 * speelde élke tab (state-gedreven) — maar met 8+ open office-tabs gaf dat
 * een koor van beltonen, en tabs met een verouderde apparaat-cache of oude
 * bundle rinkelden op het verkeerde apparaat of stopten niet.
 *
 * Nu: de state is nog steeds leidend ('ringing' inbound → beltoon, 'dialing'
 * outbound → ringback, anders stilte), maar het AFSPELEN loopt via een
 * Web Lock (`cinco-phone-tone`): alle tabs vragen de lock aan, de winnaar
 * speelt en houdt 'm vast zolang de toon gewenst is. Failover zit ingebakken:
 *  - Sluit de spelende tab → lock komt vrij → volgende tab in de wachtrij
 *    checkt de state en neemt het over.
 *  - Kan de winnaar niet hoorbaar spelen (AudioContext blijft 'suspended'
 *    door autoplay-policy) → hij geeft de lock direct terug en de volgende
 *    tab probeert het. `playRingtone`/`playRingback` rapporteren dat via hun
 *    boolean; de resume-race in audio.ts voorkomt dat een stille tab blijft
 *    hangen op een eeuwig-pendende resume().
 * Browsers zonder Web Locks vallen terug op het oude elke-tab-speelt-gedrag.
 */

import * as phoneState from './phone-state';
import { playRingtone, stopRingtone, playRingback, stopRingback } from './audio';

type Tone = 'none' | 'ringtone' | 'ringback';

const TONE_LOCK = 'cinco-phone-tone';

let active: Tone = 'none';
/** Bumped bij elke gewenste-toon-wissel; async werk checkt hiertegen. */
let episode = 0;
let wired = false;
/** Resolver die de vastgehouden lock vrijgeeft (alleen op de spelende tab). */
let releaseLock: (() => void) | null = null;
/** Annuleert een nog niet toegekende lock-aanvraag (wachtrij-tab). */
let pendingAbort: AbortController | null = null;

function desiredTone(): Tone {
  const calls = phoneState.getCalls();
  // Zodra er een gesprek daadwerkelijk loopt (opgenomen/actief), zwijgt elke
  // beltoon — óók als een dubbel/seed-record nog in 'ringing' hangt (bv. een
  // Realtime-seed die niet met de SDK-call merget). Zonder deze guard bleef de
  // telefoon doorrinkelen nadat je al had opgenomen.
  const hasLiveCall = calls.some(
    (c) => c.state === 'active' || c.answeredAt !== null,
  );
  if (hasLiveCall) return 'none';

  if (calls.some((c) => c.direction === 'inbound' && c.state === 'ringing')) {
    return 'ringtone';
  }
  if (calls.some((c) => c.direction === 'outbound' && c.state === 'dialing')) {
    return 'ringback';
  }
  return 'none';
}

function silence(): void {
  stopRingtone();
  stopRingback();
  if (releaseLock) {
    releaseLock();
    releaseLock = null;
  }
  if (pendingAbort) {
    pendingAbort.abort();
    pendingAbort = null;
  }
}

function apply(): void {
  const next = desiredTone();
  if (next === active) return;
  active = next;
  episode += 1;
  silence();
  if (next === 'none') return;
  void acquireAndPlay(next, episode);
}

async function acquireAndPlay(tone: Exclude<Tone, 'none'>, ep: number): Promise<void> {
  const play = tone === 'ringtone' ? playRingtone : playRingback;
  const stop = tone === 'ringtone' ? stopRingtone : stopRingback;

  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) {
    // Fallback zonder Web Locks: speel gewoon op deze tab.
    void play();
    return;
  }

  const controller = new AbortController();
  pendingAbort = controller;
  try {
    await locks.request(TONE_LOCK, { signal: controller.signal }, async () => {
      pendingAbort = null;
      // De grant kan (veel) later komen dan de aanvraag — alleen spelen als
      // deze toon-episode nog actueel is.
      if (ep !== episode) return;
      const audible = await play();
      if (ep !== episode) {
        stop();
        return;
      }
      if (!audible) {
        // Deze tab mag niet klinken (autoplay-suspended of sink stuk):
        // lock teruggeven zodat de volgende tab het probeert.
        stop();
        return;
      }
      // Hoorbaar — lock vasthouden tot de toon niet meer gewenst is.
      await new Promise<void>((res) => {
        releaseLock = res;
      });
    });
  } catch {
    /* aborted — gewenste toon wijzigde voordat de lock toegekend werd */
  }
}

/** Wire once per tab; called from initTelephony. */
export function initToneDriver(): void {
  if (wired) return;
  wired = true;
  phoneState.subscribe(apply);
}
